import React, { useState, useEffect, useCallback } from "react";
import {
  Banner,
  Badge,
  Button,
  Card,
  DataTable,
  Modal,
  Pagination,
  Select,
  Stack,
  Tabs,
  TextField,
  TextStyle,
  Spinner,
  FormLayout
} from "@shopify/polaris";
import { fetchData, fetcher } from "util/fetch";

// Sub-tab indices
const TAB_NEEDS_REVIEW = 0;
const TAB_ALL          = 1;

const CONFIDENCE_BADGE = {
  high:   "success",
  medium: "attention",
  low:    "warning",
};

export default function MultipackMappingsTable() {
  // ── Sub-tab ─────────────────────────────────────────────────────────────────
  const [selectedTab, setSelectedTab] = useState(TAB_NEEDS_REVIEW);

  // ── List state ───────────────────────────────────────────────────────────────
  const [mappings,      setMappings]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [errorBanner,   setErrorBanner]   = useState(null);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [sourceFilter,  setSourceFilter]  = useState("");
  const [page,          setPage]          = useState(1);
  const [totalPages,    setTotalPages]    = useState(1);
  const [totalMappings, setTotalMappings] = useState(0);

  // ── Edit/create modal ────────────────────────────────────────────────────────
  const [isModalOpen,    setIsModalOpen]    = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({
    multipack_upc:          "",
    brand:                  "",
    pack_count:             "6",
    deposit_cents_per_unit: "10",
    crf_cents_per_unit:     "0",
    bottle_deposit_id:      "",
    notes:                  ""
  });
  const [formSaving, setFormSaving] = useState(false);

  // ── Verify-only loading state (row-level) ─────────────────────────────────
  const [verifyingId, setVerifyingId] = useState(null);

  // ── Delete loading state (row-level) ──────────────────────────────────────
  const [deletingId, setDeletingId] = useState(null);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const parseErrorMessage = async (res, fallback) => {
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await res.json().catch(() => null);
      if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors.join(", ");
      if (typeof data?.error   === "string" && data.error.trim())   return data.error;
      if (typeof data?.message === "string" && data.message.trim()) return data.message;
    }
    const text = await res.text().catch(() => "");
    if (text && !text.trim().startsWith("<")) return text.slice(0, 300);
    return fallback;
  };

  const formatCents = (cents) => (cents != null ? `$${(cents / 100).toFixed(2)}` : "—");

  /** @param {Record<string, unknown> | null | undefined} d */
  const linkedDepositSummary = (d) => {
    if (!d || typeof d !== "object") return null;
    const brand   = d.brand || "";
    const flavour = d.flavour ? ` · ${d.flavour}` : "";
    const upc     = d.upc || d.normalized_upc || "";
    const ct      = (d.container_type || "").toString().trim();
    const ctShort = ct.length > 72 ? `${ct.slice(0, 69)}…` : ct;
    return { headline: `${brand}${flavour}`.trim() || "Return-It row", upc, ctShort };
  };

  // ── Loaders ──────────────────────────────────────────────────────────────────
  const loadMappings = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page });
    if (searchQuery)  params.set("q",      searchQuery);
    if (sourceFilter) params.set("source", sourceFilter);

    // Needs-Review tab always requests verified=false
    if (selectedTab === TAB_NEEDS_REVIEW) params.set("verified", "false");

    fetchData(
      `/api/bottle_deposit_mappings?${params}`,
      ({ mappings: rows, total, total_pages }) => {
        setMappings(rows);
        setTotalMappings(total);
        setTotalPages(total_pages);
        setErrorBanner(null);
      },
      (err) => {
        console.error(err);
        setMappings([]);
        setTotalMappings(0);
        setTotalPages(1);
        setErrorBanner("Could not load multipack mappings. Check your admin login and try again.");
      },
      () => setLoading(false)
    );
  }, [page, searchQuery, sourceFilter, selectedTab]);

  // Reset page when switching tabs or changing filters
  const handleTabSelect = (idx) => {
    setSelectedTab(idx);
    setPage(1);
    setSearchQuery("");
    setSourceFilter("");
  };

  useEffect(() => {
    const timer = setTimeout(() => { loadMappings(); }, 300);
    return () => clearTimeout(timer);
  }, [loadMappings]);

  // ── Verify (one-click) ───────────────────────────────────────────────────────
  const handleVerify = async (mapping) => {
    setVerifyingId(mapping.id);
    try {
      const res = await fetcher(`/api/bottle_deposit_mappings/${mapping.id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ bottle_deposit_mapping: { verified: true } })
      });
      if (!res.ok) {
        const message = await parseErrorMessage(res, "Failed to verify mapping.");
        setErrorBanner(message);
        return;
      }
      setErrorBanner(null);
      loadMappings();
    } catch (err) {
      console.error(err);
      setErrorBanner(err?.message || "Failed to verify mapping.");
    } finally {
      setVerifyingId(null);
    }
  };

  // ── Edit/create modal ────────────────────────────────────────────────────────
  const handleOpenModal = (mapping = null) => {
    if (mapping) {
      setEditingMapping(mapping);
      setFormData({
        multipack_upc:          mapping.multipack_upc,
        brand:                  mapping.brand || "",
        pack_count:             mapping.pack_count.toString(),
        deposit_cents_per_unit: mapping.deposit_cents_per_unit.toString(),
        crf_cents_per_unit:     mapping.crf_cents_per_unit.toString(),
        bottle_deposit_id:      mapping.bottle_deposit_id?.toString() || "",
        notes:                  mapping.notes || ""
      });
    } else {
      setEditingMapping(null);
      setFormData({
        multipack_upc:          "",
        brand:                  "",
        pack_count:             "6",
        deposit_cents_per_unit: "10",
        crf_cents_per_unit:     "0",
        bottle_deposit_id:      "",
        notes:                  ""
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    setFormSaving(true);
    try {
      const url    = editingMapping ? `/api/bottle_deposit_mappings/${editingMapping.id}` : "/api/bottle_deposit_mappings";
      const method = editingMapping ? "PUT" : "POST";
      const payload = {
        bottle_deposit_mapping: { ...formData, bottle_deposit_id: formData.bottle_deposit_id || null }
      };

      const res = await fetcher(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload)
      });

      if (!res.ok) {
        const message = await parseErrorMessage(res, "Failed to save mapping.");
        setErrorBanner(message);
        return;
      }

      setIsModalOpen(false);
      setErrorBanner(null);
      loadMappings();
    } catch (err) {
      console.error(err);
      setErrorBanner(err?.message || "An error occurred while saving");
    } finally {
      setFormSaving(false);
    }
  };

  // ── Edit-then-verify: saves form fields AND sets verified=true in one PATCH ─
  const handleSaveAndVerify = async () => {
    if (!editingMapping) return;
    setFormSaving(true);
    try {
      const payload = {
        bottle_deposit_mapping: {
          ...formData,
          bottle_deposit_id: formData.bottle_deposit_id || null,
          verified: true
        }
      };

      const res = await fetcher(`/api/bottle_deposit_mappings/${editingMapping.id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload)
      });

      if (!res.ok) {
        const message = await parseErrorMessage(res, "Failed to save and verify mapping.");
        setErrorBanner(message);
        return;
      }

      setIsModalOpen(false);
      setErrorBanner(null);
      loadMappings();
    } catch (err) {
      console.error(err);
      setErrorBanner(err?.message || "An error occurred while saving");
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this mapping?")) return;
    setDeletingId(id);
    try {
      const res = await fetcher(`/api/bottle_deposit_mappings/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const message = await parseErrorMessage(res, "Failed to delete mapping.");
        setErrorBanner(message);
        return;
      }
      setErrorBanner(null);
      loadMappings();
    } catch (err) {
      console.error(err);
      setErrorBanner(err?.message || "Failed to delete mapping");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Row builders ─────────────────────────────────────────────────────────────

  /** Needs-review rows: evidence, proposed charge, Return-It link, verify/edit/delete */
  const needsReviewRows = mappings.map((m) => {
    const link = linkedDepositSummary(m.bottle_deposit);
    const ev   = m.evidence || {};

    return [
      // UPC + confidence badge
      <Stack key={`upc-${m.id}`} vertical spacing="extraTight">
        <TextStyle variation="strong">{m.multipack_upc}</TextStyle>
        {m.confidence && (
          <Badge status={CONFIDENCE_BADGE[m.confidence] || "default"}>{m.confidence}</Badge>
        )}
        {m.shopify_domain && (
          <TextStyle variation="subdued">{m.shopify_domain}</TextStyle>
        )}
      </Stack>,

      // Brand + evidence
      <Stack key={`brand-${m.id}`} vertical spacing="extraTight">
        <span>{m.brand || "—"}</span>
        {(ev.title || ev.dept || ev.cat || ev.pack_pattern) && (
          <TextStyle variation="subdued">
            {[ev.title, ev.dept, ev.cat, ev.pack_pattern].filter(Boolean).join(" · ")}
          </TextStyle>
        )}
        {ev.source_file && (
          <TextStyle variation="subdued">from {ev.source_file}</TextStyle>
        )}
      </Stack>,

      // Pack count
      m.pack_count,

      // Return-It link summary
      link ? (
        <Stack key={`linked-${m.id}`} vertical spacing="extraTight">
          <TextStyle variation="strong">{link.headline}</TextStyle>
          <TextStyle variation="subdued">
            UPC {link.upc || "—"}
            {link.ctShort ? ` · ${link.ctShort}` : ""}
          </TextStyle>
          {m.bottle_deposit?.crf_cents != null && (
            <TextStyle variation="subdued">
              CRF {formatCents(m.bottle_deposit.crf_cents)}/unit
            </TextStyle>
          )}
        </Stack>
      ) : (
        <TextStyle key={`unlinked-${m.id}`} variation="subdued">Not linked</TextStyle>
      ),

      // Proposed per-unit deposit / CRF
      `${formatCents(m.deposit_cents_per_unit)} / ${formatCents(m.crf_cents_per_unit)}`,

      // Proposed total charge (what will be charged per multipack)
      <TextStyle key={`total-${m.id}`} variation="strong">
        {formatCents(m.effective_total_cents)}
      </TextStyle>,

      // Actions
      <Stack key={`actions-${m.id}`} spacing="tight">
        <Button
          size="slim"
          primary
          loading={verifyingId === m.id}
          disabled={verifyingId != null || deletingId != null}
          onClick={() => handleVerify(m)}
        >
          Verify
        </Button>
        <Button size="slim" disabled={deletingId != null} onClick={() => handleOpenModal(m)}>Edit…</Button>
        <Button
          size="slim"
          destructive
          loading={deletingId === m.id}
          disabled={deletingId != null}
          onClick={() => handleDelete(m.id)}
        >
          Delete
        </Button>
      </Stack>
    ];
  });

  /** All-mappings rows (existing layout) */
  const allRows = mappings.map((m) => {
    const link = linkedDepositSummary(m.bottle_deposit);
    return [
      m.multipack_upc,
      m.brand || "—",
      m.pack_count,
      link ? (
        <Stack key={`linked-${m.id}`} vertical spacing="extraTight">
          <TextStyle variation="strong">{link.headline}</TextStyle>
          <TextStyle variation="subdued">
            UPC {link.upc || "—"}
            {link.ctShort ? ` · ${link.ctShort}` : ""}
          </TextStyle>
          {m.bottle_deposit_id != null && (
            <TextStyle variation="subdued">DB id {m.bottle_deposit_id}</TextStyle>
          )}
        </Stack>
      ) : (
        <TextStyle key={`unlinked-${m.id}`} variation="subdued">Not linked</TextStyle>
      ),
      `${formatCents(m.deposit_cents_per_unit)} / ${formatCents(m.crf_cents_per_unit)}`,
      formatCents(m.effective_total_cents),
      <Badge key={`verified-${m.id}`} status={m.verified ? "success" : "attention"}>
        {m.verified ? "Verified" : "Needs review"}
      </Badge>,
      <Badge key={`source-${m.id}`} status={m.source === "manual" ? "new" : "info"}>
        {m.source === "auto_import" ? "Auto (Import)" : m.source === "auto_cart" ? "Auto (Cart)" : "Manual"}
      </Badge>,
      m.notes || "—",
      <Stack key={`actions-${m.id}`} spacing="tight">
        <Button size="slim" disabled={deletingId != null} onClick={() => handleOpenModal(m)}>Edit</Button>
        <Button
          size="slim"
          destructive
          loading={deletingId === m.id}
          disabled={deletingId != null}
          onClick={() => handleDelete(m.id)}
        >
          Delete
        </Button>
      </Stack>
    ];
  });

  // ── Shared filter bar ─────────────────────────────────────────────────────────
  const filterBar = (
    <Card.Section>
      <Stack>
        <Stack.Item fill>
          <TextField
            label="Search"
            labelHidden
            placeholder="Search by UPC or brand…"
            value={searchQuery}
            onChange={(val) => { setSearchQuery(val); setPage(1); }}
            clearButton
            onClearButtonClick={() => { setSearchQuery(""); setPage(1); }}
          />
        </Stack.Item>
        {selectedTab === TAB_ALL && (
          <Select
            label="Source Filter"
            labelHidden
            options={[
              { label: "All sources",                value: ""            },
              { label: "Manual",                     value: "manual"      },
              { label: "Auto-detected (Import)",     value: "auto_import" },
              { label: "Auto-detected (Cart)",       value: "auto_cart"   }
            ]}
            value={sourceFilter}
            onChange={(val) => { setSourceFilter(val); setPage(1); }}
          />
        )}
      </Stack>
    </Card.Section>
  );

  return (
    <Card
      title={`Multipack Mappings (${totalMappings})`}
      actions={selectedTab === TAB_ALL ? [{ content: "Add Mapping", onAction: () => handleOpenModal() }] : []}
    >
      {errorBanner && (
        <Card.Section>
          <Banner status="critical" onDismiss={() => setErrorBanner(null)}>
            <p>{errorBanner}</p>
          </Banner>
        </Card.Section>
      )}

      <Tabs
        tabs={[
          { id: "needs-review", content: "Needs Review" },
          { id: "all",          content: "All Mappings"  }
        ]}
        selected={selectedTab}
        onSelect={handleTabSelect}
      >
        {filterBar}

        <Card.Section flush>
          {loading ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <Spinner size="small" />
            </div>
          ) : mappings.length === 0 ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <TextStyle variation="subdued">
                {selectedTab === TAB_NEEDS_REVIEW
                  ? "No mappings pending review. All candidates have been verified."
                  : "No mappings found."}
              </TextStyle>
            </div>
          ) : selectedTab === TAB_NEEDS_REVIEW ? (
            <DataTable
              columnContentTypes={["text", "text", "numeric", "text", "text", "numeric", "text"]}
              headings={[
                "Multipack UPC / Confidence",
                "Brand / Evidence",
                "Pack count",
                "Return-It link",
                "Deposit / CRF per unit",
                "Proposed total charge",
                "Actions"
              ]}
              rows={needsReviewRows}
            />
          ) : (
            <DataTable
              columnContentTypes={["text", "text", "numeric", "text", "text", "numeric", "text", "text", "text", "text"]}
              headings={[
                "Multipack UPC",
                "Brand",
                "Pack count",
                "Return-It (single unit)",
                "Deposit / CRF per unit",
                "Total fee",
                "Status",
                "Source",
                "Notes",
                "Actions"
              ]}
              rows={allRows}
            />
          )}
        </Card.Section>

        {totalPages > 1 && (
          <Card.Section>
            <Stack distribution="center">
              <Pagination
                hasPrevious={page > 1}
                onPrevious={() => setPage((p) => p - 1)}
                hasNext={page < totalPages}
                onNext={() => setPage((p) => p + 1)}
              />
            </Stack>
          </Card.Section>
        )}
      </Tabs>

      {/* Edit / Create modal */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingMapping ? "Edit Multipack Mapping" : "Add Multipack Mapping"}
        primaryAction={
          editingMapping && !editingMapping.verified
            ? { content: "Save & Verify", onAction: handleSaveAndVerify, loading: formSaving }
            : { content: "Save",           onAction: handleSave,          loading: formSaving }
        }
        secondaryActions={[
          // When in review mode, offer Save without verifying too
          ...(editingMapping && !editingMapping.verified
            ? [{ content: "Save without verifying", onAction: handleSave, loading: formSaving }]
            : []),
          { content: "Cancel", onAction: () => setIsModalOpen(false) }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {/* Confidence/evidence banner for review candidates */}
            {editingMapping && !editingMapping.verified && (
              <Banner status="warning">
                <p>
                  <TextStyle variation="strong">Needs review</TextStyle>
                  {editingMapping.confidence ? ` — ${editingMapping.confidence} confidence` : ""}
                </p>
                {(() => {
                  const ev = editingMapping.evidence || {};
                  const parts = [ev.title, ev.dept, ev.cat, ev.pack_pattern, ev.source_file]
                    .filter(Boolean);
                  return parts.length > 0 ? (
                    <p style={{ marginTop: "0.35rem" }}>
                      <TextStyle variation="subdued">Evidence: {parts.join(" · ")}</TextStyle>
                    </p>
                  ) : null;
                })()}
              </Banner>
            )}

            {editingMapping?.bottle_deposit && (
              <Banner status="info">
                <p>
                  <TextStyle variation="strong">Linked Return-It row</TextStyle>
                  {" — "}
                  {editingMapping.bottle_deposit.brand}
                  {editingMapping.bottle_deposit.flavour
                    ? ` (${editingMapping.bottle_deposit.flavour})`
                    : ""}
                  {" · UPC "}
                  {editingMapping.bottle_deposit.upc || editingMapping.bottle_deposit.normalized_upc || "—"}
                </p>
                {editingMapping.bottle_deposit.container_type && (
                  <p style={{ marginTop: "0.35rem" }}>
                    <TextStyle variation="subdued">
                      {String(editingMapping.bottle_deposit.container_type)}
                    </TextStyle>
                  </p>
                )}
                {editingMapping.bottle_deposit.crf_cents != null && (
                  <p style={{ marginTop: "0.35rem" }}>
                    <TextStyle variation="subdued">
                      CRF: {formatCents(editingMapping.bottle_deposit.crf_cents)}/unit
                    </TextStyle>
                  </p>
                )}
              </Banner>
            )}

            <FormLayout.Group>
              <TextField
                label="Multipack UPC"
                value={formData.multipack_upc}
                onChange={(val) => setFormData({ ...formData, multipack_upc: val })}
              />
              <TextField
                label="Brand"
                value={formData.brand}
                onChange={(val) => setFormData({ ...formData, brand: val })}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Pack Count"
                type="number"
                value={formData.pack_count}
                onChange={(val) => setFormData({ ...formData, pack_count: val })}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Deposit (cents/unit)"
                type="number"
                value={formData.deposit_cents_per_unit}
                onChange={(val) => setFormData({ ...formData, deposit_cents_per_unit: val })}
                helpText="Default: 10c"
              />
              <TextField
                label="CRF (cents/unit)"
                type="number"
                value={formData.crf_cents_per_unit}
                onChange={(val) => setFormData({ ...formData, crf_cents_per_unit: val })}
                helpText="Default: 0c"
              />
            </FormLayout.Group>

            <TextField
              label="Linked Bottle Deposit ID (Optional)"
              value={formData.bottle_deposit_id}
              onChange={(val) => setFormData({ ...formData, bottle_deposit_id: val })}
              helpText="If provided, overrides the per-unit deposit/CRF above with the exact rates from the Return-It db."
            />

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(val) => setFormData({ ...formData, notes: val })}
              multiline={2}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Card>
  );
}
